import React, { useState, useEffect } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import { apiService } from '../../services/apiService';
import { posthogService } from '../../services/posthogService';
import { AnalysisLoading } from '../../components/AnalysisLoading';
import { findBestMatch } from '../../utils/utils.js';

export const Route = createFileRoute('/analysis/$sessionId')({
  component: AnalysisDashboard,
  validateSearch: (search) => ({
    from: search?.from,
    status: search?.status,
  }),
});

function AnalysisDashboard() {
  const { sessionId } = Route.useParams();
  const { from, status } = Route.useSearch();

  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ['analysis', sessionId],
    queryFn: async () => {
      try {
        return await apiService.getAnalysisDashboard(sessionId);
      } catch (err) {
        // Parse the error to determine the type
        const errorMessage = err.message || '';
        const statusCode = err.statusCode;
        
        // Create a proper Error object with custom properties
        const error = new Error(errorMessage);
        error.statusCode = statusCode;
        
        // 202 = Still processing (keep polling)
        const errorLower = errorMessage.toLowerCase();
        if (statusCode === 202 || errorLower.includes('still being generated') || errorLower.includes('processing') || errorLower.includes('recommendations are still')) {
          error.isProcessing = true;
          throw error;
        }
        
        // 404 = Not found (real error)
        if (statusCode === 404 || errorLower.includes('session not found') || errorLower.includes('not found')) {
          error.isNotFound = true;
          throw error;
        }
        
        // 500 = Server error (real error, e.g., card matching failed)
        if (statusCode === 500 || errorLower.includes('matching') || errorLower.includes('failed')) {
          error.isServerError = true;
          throw error;
        }
        
        // Default to server error
        error.isServerError = true;
        throw error;
      }
    },
    enabled: !!sessionId,
    retry: false, // Don't retry on error - we're polling instead
    refetchInterval: (query) => {
      const hasError = query.state.error;
      const hasData = query.state.data;
      
      // If we have data, stop polling
      if (hasData) return false;
      
      // If we have an error, only continue polling if it's a "processing" error (202)
      if (hasError) {
        const isStillProcessing = hasError?.isProcessing;
        const isRealError = hasError?.isNotFound || hasError?.isServerError;
        
        // Stop polling on real errors (404, 500)
        if (isRealError) {
          return false;
        }
        
        // Continue polling if still processing (202)
        if (isStillProcessing) {
          return 5000;
        }
        
        // Unknown error type - stop polling to be safe
        return false;
      }
      
      // No data and no error yet - keep polling
      return 5000;
    },
  });

  // Show loading if: initially loading, currently fetching, or analysis is still processing
  const isProcessing = error?.isProcessing;
  const isNotFound = error?.isNotFound;
  const isServerError = error?.isServerError;

  // Track loading state
  useEffect(() => {
    if ((isLoading || isFetching || isProcessing) && !data) {
      posthogService.analysisLoadingViewed(sessionId);
    }
  }, [isLoading, isFetching, isProcessing, data, sessionId]);

  // Track successful analysis view
  useEffect(() => {
    if (data && data.recommendations) {
      posthogService.analysisViewed(sessionId, data.recommendations.length);
    }
  }, [data, sessionId]);

  // Show different loading screens based on context
  if ((isLoading || isFetching || isProcessing) && !data) {
    // If coming from recommendations list with status 'done', show quick loading
    if (from === 'list' && status === 'done') {
      return (
        <div className='bg-white rounded-2xl p-8 shadow-xl text-center'>
          <div className='flex flex-col items-center justify-center gap-4'>
            <div className='w-8 h-8 border-3 border-gray-300 border-t-gray-900 rounded-full animate-spin'></div>
            <p className='text-sm text-gray-600'>Loading your recommendations...</p>
          </div>
        </div>
      );
    }
    
    // If status is 'processing' or unknown, show full loading screen with tips
    return <AnalysisLoading message="Crafting your personalized recommendations..." />;
  }

  // Show specific error messages for different error types
  if (error && !isProcessing && !data) {
    if (isNotFound) {
      return (
        <div className="bg-white rounded-2xl p-10 shadow-xl text-center">
          <div className="text-red-500 mb-4 text-lg font-medium">Session Not Found</div>
          <p className="text-gray-600">This analysis session doesn't exist or has expired.</p>
        </div>
      );
    }
    
    if (isServerError) {
      return (
        <div className="bg-white rounded-2xl p-10 shadow-xl text-center">
          <div className="text-red-500 mb-4 text-lg font-medium">Analysis Error</div>
          <p className="text-gray-600 mb-4">
            We encountered an issue generating your recommendations. Our team has been notified.
          </p>
          <p className="text-sm text-gray-500">
            Please try starting a new session or contact support if the issue persists.
          </p>
        </div>
      );
    }
    
    return (
      <div className="bg-white rounded-2xl p-10 shadow-xl text-center">
        <div className="text-red-500 mb-4 text-lg font-medium">Unexpected Error</div>
        <p className="text-gray-600">Something went wrong. Please try again later.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-20">
      {/* Analysis Date Header */}
      {data?.analysis_metadata?.analysis_date && (
        <div className="bg-white rounded-2xl p-4 shadow-xl">
          <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span>Analysis completed on {new Date(data.analysis_metadata.analysis_date).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}</span>
          </div>
        </div>
      )}

      {data?.user_profile && <ProfileSummary profile={data.user_profile} />}

      {data?.recommendations && data.recommendations.length > 0 && (
        <RecommendationsList
          recommendations={data.recommendations}
          cardsData={data.cards_data || {}}
        />
      )}

      {data?.enricher_data && <EnricherSummary enricherData={data.enricher_data} />}

      {data?.analysis_metadata && <AnalysisMetadata metadata={data.analysis_metadata} />}

      {(!data?.recommendations || data.recommendations.length === 0) && (
        <div className="bg-white rounded-2xl p-8 shadow-xl text-center">
          <div className="text-gray-500">No recommendations available</div>
        </div>
      )}
    </div>
  );
}

function ProfileSummary({ profile }) {
  if (!profile) return null;

  return (
    <div className="bg-white rounded-2xl p-8 shadow-xl">
      <div className="text-sm text-gray-500 mb-2 uppercase tracking-wide">Your Profile</div>
      <h1 className="text-2xl font-light text-gray-900 mb-6">Complete Analysis</h1>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {profile.monthly_salary && (
          <ProfileItem label="Income" value={formatSalary(profile.monthly_salary)} />
        )}
        {profile.monthly_spending && (
          <ProfileItem label="Spending" value={formatSpending(profile.monthly_spending)} />
        )}
        {profile.employment_type && (
          <ProfileItem label="Employment" value={formatEmployment(profile.employment_type)} />
        )}
        {profile.priorities && profile.priorities.length > 0 && (
          <ProfileItem label="Priority" value={formatPriority(profile.priorities[0])} />
        )}
      </div>
    </div>
  );
}

function ProfileItem({ label, value }) {
  return (
    <div>
      <div className="text-xs text-gray-500 mb-1 uppercase tracking-wider">{label}</div>
      <div className="text-sm font-medium text-gray-900">{value || 'Not specified'}</div>
    </div>
  );
}

function RecommendationsList({ recommendations, cardsData }) {
  if (!recommendations || recommendations.length === 0) return null;

  return (
    <div className="space-y-6">
      <div className="text-sm text-gray-500 uppercase tracking-wide">
        Your Top {recommendations.length} Matches
      </div>

      {recommendations.map((rec, index) => {
        const cardData = cardsData?.[rec.card_name] ||
                  cardsData?.[findBestMatch(rec.card_name, Object.keys(cardsData || {}))];

        return (
          <RecommendationCard
            key={rec.card_name || `card-${index}`}
            recommendation={rec}
            cardData={cardData}
            isTopPick={index === 0}
          />
        );
      })}
    </div>
  );
}

function RecommendationCard({ recommendation, cardData, isTopPick }) {
  const [showFullDetails, setShowFullDetails] = useState(false);

  if (!recommendation) return null;

  const safeCardData = cardData?.raw_data || {};
  const safeFees = safeCardData.fees || {};
  const safeAPR = safeCardData.apr || {};
  const safeEligibility = safeCardData.eligibility || {};
  const safeBenefits = safeCardData.benefits || [];
  const safeWelcomeBonus = safeCardData.welcome_bonus || {};
  const cardTier = safeCardData.card_tier || '';
  const loungeAccess = safeCardData.lounge_access || null;
  const insuranceCoverage = safeCardData.insurance_coverage || [];
  const cardFeatures = safeCardData.card_features || [];
  const promotionalOffers = safeCardData.promotional_offers || [];
  const paymentTerms = safeCardData.payment_terms || {};
  const creditLimitInfo = safeCardData.credit_limit_info || {};
  
  // Check multiple possible URL locations in order of preference
  const sourceURL = 
    safeCardData?.apply_url || 
    safeCardData?.links?.landing_page_url || 
    safeCardData?.metadata?.source_url || 
    recommendation?.metadata?.source_url || 
    "";

  return (
    <div className={`bg-white rounded-2xl overflow-hidden shadow-xl ${isTopPick ? 'ring-2 ring-blue-500' : ''}`}>
      {isTopPick && (
        <div className="bg-blue-500 text-white text-center py-2 text-sm font-medium">
          Best Match - Highest Value for Your Profile
        </div>
      )}

      {/* Card Header */}
      <div className="p-8 bg-gray-50">
        <div className="flex justify-between items-start mb-6">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <div className="text-sm text-gray-500 uppercase tracking-wide">
                {recommendation.issuer || 'Unknown Bank'}
              </div>
              {cardTier && cardTier !== 'standard' && (
                <CardTierBadge tier={cardTier} />
              )}
            </div>
            <h3 className="text-xl font-light text-gray-900 mb-2">
              {recommendation.card_name || 'Credit Card'}
            </h3>
            <div className="flex items-center gap-3">
              <div className="text-sm text-gray-600">
                {safeCardData.network || safeCardData.issuer || 'Credit Card'}
              </div>
              {loungeAccess?.included && (
                <div className="flex items-center gap-1 text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded-full">
                  <span>✈️</span>
                  <span>Lounge Access</span>
                </div>
              )}
            </div>
          </div>
          <div className="text-right">
            <div className="text-2xl font-light text-gray-900">
              {recommendation.confidence_card_level_0_to_100 ? Math.round(recommendation.confidence_card_level_0_to_100) : '0'}%
            </div>
            <div className="text-xs text-gray-500 uppercase tracking-wide">Match</div>
          </div>
        </div>

        {/* Key Metrics Grid - Adaptive */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <MetricItem
            label="Monthly Value"
            value={recommendation.net_value_aed_per_year ? `AED ${Math.round(recommendation.net_value_aed_per_year / 12).toLocaleString()}` : 'N/A'}
          />
          <MetricItem
            label="Annual Cost"
            value={recommendation.estimated_cost_aed_per_year ? `AED ${Math.round(recommendation.estimated_cost_aed_per_year).toLocaleString()}` : formatAnnualFee(safeFees.annual_fee)}
          />
          {safeAPR.max && (
            <MetricItem
              label="APR"
              value={`${safeAPR.max}%`}
            />
          )}
          {safeFees.fx_fee && (
            <MetricItem
              label="FX Fee"
              value={safeFees.fx_fee}
            />
          )}
        </div>
      </div>

      {/* AI Analysis Section - Only if reasoning exists */}
      {recommendation.reasoning_trace && recommendation.reasoning_trace.length > 0 && (
        <div className="p-8 border-b border-gray-200">
          <h4 className="text-sm font-medium text-gray-900 mb-3 uppercase tracking-wide">
            Why This Card Matches You
          </h4>
          <div className="space-y-3 mb-4">
            {recommendation.reasoning_trace.map((step, idx) => (
              <p key={idx} className="text-sm text-gray-600 leading-relaxed">
                {step}
              </p>
            ))}
          </div>

          {/* Key Features - Only if they exist */}
          {recommendation.key_features && recommendation.key_features.length > 0 && (
            <div className="grid md:grid-cols-2 gap-4">
              {recommendation.key_features.slice(0, 4).map((benefit, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                  <div className="text-sm text-gray-600">{benefit}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Financial Details - Only if data exists */}
      {(Object.keys(safeFees).length > 0 || Object.keys(safeAPR).length > 0) && (
        <div className="p-8 bg-gray-50">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Fees Breakdown - Only if fees exist */}
            {Object.keys(safeFees).length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-4 uppercase tracking-wide">
                  Fee Structure
                </h4>
                <div className="space-y-2">
                  {safeFees.annual_fee !== undefined && (
                    <FeeItem label="Annual Fee" value={formatFee(safeFees.annual_fee)} />
                  )}
                  {safeFees.late_payment_fee && (
                    <FeeItem label="Late Payment" value={safeFees.late_payment_fee} />
                  )}
                  {safeFees.cash_advance_fee && (
                    <FeeItem label="Cash Advance" value={safeFees.cash_advance_fee} />
                  )}
                  {safeFees.overlimit_fee && (
                    <FeeItem label="Over Limit" value={safeFees.overlimit_fee} />
                  )}
                  {safeFees.fx_fee && (
                    <FeeItem label="Foreign Currency" value={safeFees.fx_fee} />
                  )}
                  {safeFees.returned_payment_fee && (
                    <FeeItem label="Returned Payment" value={safeFees.returned_payment_fee} />
                  )}
                </div>
              </div>
            )}

            {/* APR Details - Only if APR data exists */}
            {Object.keys(safeAPR).length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-4 uppercase tracking-wide">
                  Interest Rates (APR)
                </h4>
                <div className="space-y-2">
                  {safeAPR.max && <APRItem label="Purchase APR" value={safeAPR.max} />}
                  {safeAPR.cash_advance_apr && <APRItem label="Cash Advance APR" value={safeAPR.cash_advance_apr} />}
                  {safeAPR.penalty_apr && <APRItem label="Penalty APR" value={safeAPR.penalty_apr} />}
                  {safeAPR.balance_transfer_apr && <APRItem label="Balance Transfer APR" value={safeAPR.balance_transfer_apr} />}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Eligibility Requirements - Only if data exists */}
      {Object.keys(safeEligibility).length > 0 && (
        <div className="p-8 border-b border-gray-200">
          <h4 className="text-sm font-medium text-gray-900 mb-4 uppercase tracking-wide">
            Eligibility Requirements
          </h4>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <div className="space-y-2">
                {safeEligibility.min_income && (
                  <EligibilityItem
                    label="Minimum Income"
                    value={formatIncome(safeEligibility.min_income)}
                  />
                )}
                {safeEligibility.age_range && (
                  <EligibilityItem
                    label="Age Requirement"
                    value={safeEligibility.age_range}
                  />
                )}
                {safeEligibility.residency && (
                  <EligibilityItem
                    label="Residency"
                    value={safeEligibility.residency}
                  />
                )}
              </div>
            </div>
            <div>
              {safeEligibility.other && (
                <div className="text-sm text-gray-600">
                  <div className="font-medium text-gray-900 mb-1">Additional Requirements:</div>
                  {safeEligibility.other}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Benefits Section - Only if benefits exist */}
      {safeBenefits.length > 0 && (
        <div className="p-8 border-b border-gray-200">
          <h4 className="text-sm font-medium text-gray-900 mb-4 uppercase tracking-wide">
            Complete Benefits Package
          </h4>
          <div className="grid md:grid-cols-2 gap-2">
            {safeBenefits.map((benefit, idx) => (
              <div key={idx} className="flex items-start gap-3 py-1">
                <div className="w-1 h-1 bg-gray-400 rounded-full mt-2 flex-shrink-0"></div>
                <div className="text-sm text-gray-600">{benefit}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Welcome Bonus - Only if exists and has value */}
      {safeWelcomeBonus.amount && safeWelcomeBonus.amount > 0 && (
        <WelcomeBonusSection welcomeBonus={safeWelcomeBonus} />
      )}

      {/* NEW: Lounge Access Section */}
      <LoungeAccessSection loungeAccess={loungeAccess} />

      {/* NEW: Card Features Section */}
      <CardFeaturesSection features={cardFeatures} />

      {/* NEW: Promotional Offers Section */}
      <PromotionalOffersSection offers={promotionalOffers} />

      {/* NEW: Payment Terms Section */}
      <PaymentTermsSection paymentTerms={paymentTerms} />

      {/* NEW: Structured Insurance Coverage Section */}
      <StructuredInsuranceCoverageSection coverage={insuranceCoverage} />

      {/* Insurance Coverage - Only if exists (keep old for backward compatibility) */}
      {safeBenefits.some(b => b.toLowerCase().includes('insurance')) && insuranceCoverage.length === 0 && (
        <InsuranceCoverageSection benefits={safeBenefits} />
      )}

      {/* Action Buttons */}
      <div className="p-8">
        <div className="flex gap-4 mb-4">
          {sourceURL ? (
            <a 
              href={sourceURL} 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex-1 bg-gray-900 text-white py-3 px-6 rounded-lg font-medium hover:bg-gray-800 transition-colors text-center"
              onClick={() => posthogService.recommendationClicked(recommendation.card_name, recommendation.issuer)}
            >
              Visit Bank Website
            </a>
          ) : (
            <button
              disabled
              className="flex-1 bg-gray-300 text-gray-500 py-3 px-6 rounded-lg font-medium cursor-not-allowed text-center"
            >
              Website Not Available
            </button>
          )}
          <button
            disabled={!safeCardData.disclaimers && !safeCardData.metadata?.notes && !safeFees.other_fees}
            onClick={() => setShowFullDetails(!showFullDetails)}
            className={`cursor-pointer flex-1 py-3 px-6 rounded-lg font-medium transition-colors ${
              (!safeCardData.disclaimers && !safeCardData.metadata?.notes && !safeFees.other_fees)
                ? 'border border-gray-200 text-gray-400 bg-gray-100 cursor-not-allowed'
                : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            {showFullDetails ? 'Hide Details' : 'View All Terms'}
          </button>
        </div>
      </div>
      {/* Collapsible Full Terms - Only if there's data to show */}
      {showFullDetails && (safeCardData.disclaimers || safeCardData.metadata?.notes || safeFees.other_fees) && (
        <FullTermsSection cardData={safeCardData} cardUpdateTime={cardData?.updated_at} />
      )}
    </div>
  );
}

// Extracted Components for Better Organization
function EarningStructureSection({ earningStructure }) {
  if (!earningStructure) return null;

  return (
    <div className="p-8 border-b border-gray-200">
      <h4 className="text-sm font-medium text-gray-900 mb-4 uppercase tracking-wide">
        How You Earn Rewards
      </h4>
      <div className="space-y-3">
        {earningStructure.base_rate && (
          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="text-sm font-medium text-blue-900">Base Rate</div>
            <div className="text-sm text-blue-700">{earningStructure.base_rate}</div>
          </div>
        )}

        {earningStructure.bonus_categories &&
          Object.entries(earningStructure.bonus_categories).map(([key, value]) => (
            value && value !== 'null' && (
              <div key={key} className="bg-gray-50 p-4 rounded-lg">
                <div className="text-sm font-medium text-gray-900">
                  {value.split('(')[0].trim()}
                </div>
                <div className="text-sm text-gray-600">
                  {value.includes('(') ? value.substring(value.indexOf('(')) : 'Bonus rate'}
                </div>
              </div>
            )
          ))
        }

        {earningStructure.spending_caps && (
          <div className="text-xs text-gray-500 mt-2 bg-yellow-50 p-2 rounded">
            <strong>Cap:</strong> {earningStructure.spending_caps}
          </div>
        )}
      </div>
    </div>
  );
}

function WelcomeBonusSection({ welcomeBonus }) {
  return (
    <div className="p-8 border-b border-gray-200 bg-green-50">
      <h4 className="text-sm font-medium text-gray-900 mb-4 uppercase tracking-wide">
        Welcome Bonus
      </h4>
      <div className="space-y-2">
        <div className="text-sm">
          <span className="font-medium">Bonus: </span>
          {welcomeBonus.amount} {welcomeBonus.currency || ''}
        </div>
        {welcomeBonus.conditions && (
          <div className="text-xs text-gray-600">
            <strong>Conditions:</strong> {welcomeBonus.conditions}
          </div>
        )}
        {welcomeBonus.validity && (
          <div className="text-xs text-gray-600">
            <strong>Validity:</strong> {welcomeBonus.validity}
          </div>
        )}
      </div>
    </div>
  );
}

function InsuranceCoverageSection({ benefits }) {
  const insuranceBenefits = benefits.filter(b => b.toLowerCase().includes('insurance'));

  if (insuranceBenefits.length === 0) return null;

  return (
    <div className="p-8 border-b border-gray-200">
      <h4 className="text-sm font-medium text-gray-900 mb-4 uppercase tracking-wide">
        Insurance Coverage
      </h4>
      <div className="space-y-2">
        {insuranceBenefits.map((insurance, idx) => (
          <div key={idx} className="text-sm text-gray-600 bg-blue-50 p-3 rounded">
            {insurance}
          </div>
        ))}
      </div>
    </div>
  );
}

function FullTermsSection({ cardData, cardUpdateTime }) {
  return (
    <div className="p-8 bg-gray-50 border-t border-gray-200">
      <h4 className="text-sm font-medium text-gray-900 mb-4 uppercase tracking-wide">
        Complete Terms & Conditions
      </h4>
      <div className="text-xs text-gray-600 space-y-3">
        {cardData.disclaimers && (
          <div>
            <strong>Disclaimers:</strong>
            <p className="mt-1">{cardData.disclaimers}</p>
          </div>
        )}

        {cardData.metadata?.notes && (
          <div>
            <strong>Additional Notes:</strong>
            <p className="mt-1">{cardData.metadata.notes}</p>
          </div>
        )}

        {cardData.fees?.other_fees && cardData.fees.other_fees.length > 0 && (
          <div>
            <strong>Other Fees:</strong>
            <ul className="mt-1 space-y-1">
              {cardData.fees.other_fees.map((fee, idx) => (
                <li key={idx}>
                  <strong>{fee.name}:</strong> {fee.amount}
                  {fee.notes && <span className="text-gray-500"> - {fee.notes}</span>}
                </li>
              ))}
            </ul>
          </div>
        )}

        {cardUpdateTime && (
          <div className="pt-2 text-gray-500 border-t border-gray-200">
            Data last updated: {new Date(cardUpdateTime).toLocaleDateString()}
          </div>
        )}
      </div>
    </div>
  );
}

// Helper Components
function MetricItem({ label, value }) {
  return (
    <div className="text-center">
      <div className="text-lg font-light text-gray-900 mb-1">{value || 'N/A'}</div>
      <div className="text-xs text-gray-500 uppercase tracking-wide">{label}</div>
    </div>
  );
}

function FeeItem({ label, value }) {
  return (
    <div className="flex justify-between text-sm py-1">
      <span className="text-gray-600">{label}</span>
      <span className="text-gray-900 font-medium">{value || 'N/A'}</span>
    </div>
  );
}

function APRItem({ label, value }) {
  return (
    <div className="flex justify-between text-sm py-1">
      <span className="text-gray-600">{label}</span>
      <span className="text-gray-900 font-medium">{value ? `${value}%` : 'N/A'}</span>
    </div>
  );
}

function EligibilityItem({ label, value }) {
  return (
    <div className="flex justify-between text-sm py-1">
      <span className="text-gray-600">{label}</span>
      <span className="text-gray-900 font-medium">{value || 'N/A'}</span>
    </div>
  );
}

function CardTierBadge({ tier }) {
  const tierConfig = {
    'entry': { label: 'Entry', emoji: '🌟', bg: 'bg-gray-100', text: 'text-gray-700' },
    'gold': { label: 'Gold', emoji: '🥇', bg: 'bg-yellow-100', text: 'text-yellow-800' },
    'platinum': { label: 'Platinum', emoji: '💎', bg: 'bg-gray-200', text: 'text-gray-800' },
    'signature': { label: 'Signature', emoji: '✨', bg: 'bg-purple-100', text: 'text-purple-800' },
    'infinite': { label: 'Infinite', emoji: '♾️', bg: 'bg-blue-100', text: 'text-blue-800' },
    'world_elite': { label: 'World Elite', emoji: '👑', bg: 'bg-indigo-100', text: 'text-indigo-800' },
    'centurion': { label: 'Centurion', emoji: '🏆', bg: 'bg-black', text: 'text-white' }
  };

  const config = tierConfig[tier] || tierConfig['entry'];

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
      <span>{config.emoji}</span>
      <span>{config.label}</span>
    </span>
  );
}

function LoungeAccessSection({ loungeAccess }) {
  if (!loungeAccess?.included) return null;

  return (
    <div className="p-8 border-b border-gray-200 bg-blue-50">
      <h4 className="text-sm font-medium text-gray-900 mb-4 uppercase tracking-wide flex items-center gap-2">
        <span>✈️</span>
        <span>Airport Lounge Access</span>
      </h4>
      <div className="space-y-3">
        <div className="grid md:grid-cols-2 gap-4">
          {loungeAccess.visits_per_year && (
            <div className="flex items-start gap-3">
              <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
              <div className="text-sm">
                <div className="font-medium text-gray-900">Visits per Year</div>
                <div className="text-gray-600">
                  {loungeAccess.visits_per_year === 'unlimited' 
                    ? 'Unlimited' 
                    : `${loungeAccess.visits_per_year} complimentary visits`}
                </div>
              </div>
            </div>
          )}
          
          {loungeAccess.guest_allowed !== null && (
            <div className="flex items-start gap-3">
              <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
              <div className="text-sm">
                <div className="font-medium text-gray-900">Guest Policy</div>
                <div className="text-gray-600">
                  {loungeAccess.guest_allowed ? 'Guest allowed' : 'Cardholder only'}
                </div>
              </div>
            </div>
          )}
        </div>

        {loungeAccess.networks && loungeAccess.networks.length > 0 && (
          <div className="flex items-start gap-3">
            <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
            <div className="text-sm">
              <div className="font-medium text-gray-900">Networks</div>
              <div className="text-gray-600">{loungeAccess.networks.join(', ')}</div>
            </div>
          </div>
        )}

        {loungeAccess.conditions && (
          <div className="text-xs text-gray-600 bg-white p-3 rounded">
            <strong>Conditions:</strong> {loungeAccess.conditions}
          </div>
        )}
      </div>
    </div>
  );
}

function CardFeaturesSection({ features }) {
  if (!features || features.length === 0) return null;

  return (
    <div className="p-8 border-b border-gray-200">
      <h4 className="text-sm font-medium text-gray-900 mb-4 uppercase tracking-wide">
        Card Features
      </h4>
      <div className="grid md:grid-cols-2 gap-2">
        {features.map((feature, idx) => (
          <div key={idx} className="flex items-start gap-3">
            <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-2 flex-shrink-0"></div>
            <div className="text-sm text-gray-600">{feature}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PromotionalOffersSection({ offers }) {
  if (!offers || offers.length === 0) return null;

  return (
    <div className="p-8 border-b border-gray-200 bg-green-50">
      <h4 className="text-sm font-medium text-gray-900 mb-4 uppercase tracking-wide flex items-center gap-2">
        <span>🎁</span>
        <span>Limited Time Offers</span>
      </h4>
      <div className="space-y-4">
        {offers.map((offer, idx) => (
          <div key={idx} className="bg-white p-4 rounded-lg border border-green-200">
            <div className="flex items-start justify-between gap-4 mb-2">
              <div className="font-medium text-gray-900">{offer.offer_type}</div>
              {offer.valid_until && (
                <div className="text-xs text-gray-500 whitespace-nowrap">
                  Until {offer.valid_until}
                </div>
              )}
            </div>
            <div className="text-sm text-gray-600 mb-2">{offer.description}</div>
            {offer.conditions && (
              <div className="text-xs text-gray-500">
                <strong>Conditions:</strong> {offer.conditions}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function PaymentTermsSection({ paymentTerms }) {
  if (!paymentTerms || Object.keys(paymentTerms).length === 0) return null;

  return (
    <div className="p-8 border-b border-gray-200">
      <h4 className="text-sm font-medium text-gray-900 mb-4 uppercase tracking-wide">
        Payment Terms
      </h4>
      <div className="grid md:grid-cols-3 gap-4">
        {paymentTerms.interest_free_period && (
          <div>
            <div className="text-xs text-gray-500 mb-1">Interest-Free Period</div>
            <div className="text-sm text-gray-900 font-medium">{paymentTerms.interest_free_period}</div>
          </div>
        )}
        {paymentTerms.grace_period && (
          <div>
            <div className="text-xs text-gray-500 mb-1">Grace Period</div>
            <div className="text-sm text-gray-900 font-medium">{paymentTerms.grace_period}</div>
          </div>
        )}
        {paymentTerms.minimum_payment && (
          <div>
            <div className="text-xs text-gray-500 mb-1">Minimum Payment</div>
            <div className="text-sm text-gray-900 font-medium">{paymentTerms.minimum_payment}</div>
          </div>
        )}
      </div>
    </div>
  );
}

function StructuredInsuranceCoverageSection({ coverage }) {
  if (!coverage || coverage.length === 0) return null;

  return (
    <div className="p-8 border-b border-gray-200 bg-purple-50">
      <h4 className="text-sm font-medium text-gray-900 mb-4 uppercase tracking-wide flex items-center gap-2">
        <span>🛡️</span>
        <span>Insurance Coverage</span>
      </h4>
      <div className="space-y-3">
        {coverage.map((insurance, idx) => (
          <div key={idx} className="bg-white p-4 rounded-lg">
            <div className="flex items-start justify-between gap-4 mb-2">
              <div className="font-medium text-gray-900">{insurance.type}</div>
              {insurance.coverage_amount && (
                <div className="text-sm font-medium text-purple-600">
                  {insurance.coverage_currency} {insurance.coverage_amount.toLocaleString()}
                </div>
              )}
            </div>
            <div className="text-sm text-gray-600">{insurance.description}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function cleanMarkdownContent(content) {
  if (!content) return '';
  
  let cleaned = content;
  
  // Remove confidence level text patterns (e.g., "confidence_0_to_100":"80" or just "80")
  cleaned = cleaned.replace(/confidence[_\s]*(?:0[_\s]*to[_\s]*100)?["\s:]*["'\d]+/gi, '');
  
  // Remove trailing JSON artifacts (e.g., "}{"  or "confidence_0_to_100\":\"80\"}")
  // These occur when the AI accidentally includes JSON structure in markdown output
  cleaned = cleaned.replace(/\}\s*\{[^}]*$/g, '');
  cleaned = cleaned.replace(/[,\}]\s*"?\w+[_\w]*"?\s*:\s*"?[\w\d]+?["'}]*$/g, '');
  
  // Remove standalone JSON objects at the end
  cleaned = cleaned.replace(/\{[\s\S]*?\}\s*$/g, '');
  
  // Remove escaped quotes and backslashes that don't belong
  cleaned = cleaned.replace(/\\"/g, '"');
  cleaned = cleaned.replace(/\\\\/g, '');
  
  // Clean up excessive whitespace and trailing artifacts
  cleaned = cleaned.replace(/\s*\}\s*$/g, ''); // Remove trailing }
  cleaned = cleaned.trim();
  
  return cleaned;
}

function EnricherSummary({ enricherData }) {
  if (!enricherData || !enricherData.plan_for_top_card) return null;

  const cleanedPlan = cleanMarkdownContent(enricherData.plan_for_top_card);
  const confidence = enricherData.confidence_0_to_100 || enricherData.confidence_overall_0_to_100;
  const confidenceNum = typeof confidence === 'string' ? parseInt(confidence) : confidence;

  // Determine confidence level and styling
  const getConfidenceLevel = (score) => {
    if (score >= 80) return { label: 'High Confidence', color: 'bg-green-100 text-green-800', dotColor: 'bg-green-500' };
    if (score >= 60) return { label: 'Medium Confidence', color: 'bg-yellow-100 text-yellow-800', dotColor: 'bg-yellow-500' };
    return { label: 'Low Confidence', color: 'bg-gray-100 text-gray-800', dotColor: 'bg-gray-500' };
  };

  const confidenceInfo = confidenceNum ? getConfidenceLevel(confidenceNum) : null;

  return (
    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 shadow-xl">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
        <div>
          <div className="text-sm text-blue-700 mb-2 uppercase tracking-wide flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
            </svg>
            Actionable Plan
          </div>
          <h3 className="text-xl font-light text-gray-900">How to Maximize Your Top Card</h3>
        </div>
        
        {confidenceInfo && (
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${confidenceInfo.color} text-xs font-medium whitespace-nowrap self-start sm:self-auto`}>
            <span className={`w-2 h-2 rounded-full ${confidenceInfo.dotColor} animate-pulse`}></span>
            <span>{confidenceInfo.label}</span>
            <span className="text-gray-600">({confidenceNum}%)</span>
          </div>
        )}
      </div>
      
      <div className="bg-white rounded-xl p-8 mb-6">
        <div className="prose prose-sm sm:prose-base max-w-none
          prose-headings:font-semibold prose-headings:text-gray-900 prose-headings:mt-8 prose-headings:mb-4 first:prose-headings:mt-0
          prose-p:text-gray-700 prose-p:leading-relaxed prose-p:mb-4
          prose-ul:my-4 prose-ul:space-y-2
          prose-li:text-gray-700 prose-li:leading-relaxed
          prose-strong:text-gray-900 prose-strong:font-semibold
          prose-a:text-blue-600 prose-a:no-underline hover:prose-a:underline
          [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
          <ReactMarkdown
            remarkPlugins={[remarkGfm, remarkBreaks]}
            components={{
              p: ({ children }) => <p className="mb-4 last:mb-0 text-sm sm:text-base">{children}</p>,
              h2: ({ children }) => <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mt-6 mb-3 first:mt-0 pb-2 border-b border-gray-200">{children}</h2>,
              h3: ({ children }) => <h3 className="text-base sm:text-lg font-semibold text-gray-900 mt-5 mb-2">{children}</h3>,
              ul: ({ children }) => <ul className="space-y-2 my-4 list-disc pl-5">{children}</ul>,
              ol: ({ children }) => <ol className="space-y-2 my-4 list-decimal pl-5">{children}</ol>,
              li: ({ children }) => <li className="leading-relaxed text-sm sm:text-base pl-2">{children}</li>,
              strong: ({ children }) => <strong className="font-semibold text-gray-900">{children}</strong>,
              a: ({ href, children }) => (
                <a href={href} className="text-blue-600 hover:text-blue-700 hover:underline" target="_blank" rel="noopener noreferrer">
                  {children}
                </a>
              ),
              code: ({ inline, children }) => 
                inline ? (
                  <code className="bg-gray-100 text-gray-900 px-1.5 py-0.5 rounded text-sm font-mono">{children}</code>
                ) : (
                  <code className="block bg-gray-100 text-gray-900 p-3 rounded text-sm font-mono overflow-x-auto my-3">{children}</code>
                ),
            }}
          >
            {cleanedPlan}
          </ReactMarkdown>
        </div>
      </div>

      {enricherData.secondary_card_tactics && enricherData.secondary_card_tactics.length > 0 && (
        <div className="mb-6">
          <h4 className="text-sm font-medium text-gray-900 mb-3 uppercase tracking-wide">
            Multi-Card Strategy
          </h4>
          <div className="space-y-3">
            {enricherData.secondary_card_tactics.map((tactic, idx) => (
              <div key={idx} className="bg-white rounded-lg p-4">
                <div className="font-medium text-gray-900 mb-1">{tactic.card_name}</div>
                <div className="text-sm text-gray-600 mb-2">{tactic.when_to_use}</div>
                {tactic.categories && tactic.categories.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {tactic.categories.map((cat, i) => (
                      <span key={i} className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                        {cat}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {enricherData.assumptions && enricherData.assumptions.length > 0 && (
        <div className="mb-4">
          <h4 className="text-xs font-medium text-gray-700 mb-2 uppercase tracking-wide">Assumptions</h4>
          <ul className="text-xs text-gray-600 space-y-1">
            {enricherData.assumptions.map((assumption, idx) => (
              <li key={idx} className="flex items-start gap-2">
                <span className="text-blue-600 mt-0.5">•</span>
                <span>{assumption}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {enricherData.confidence && (
        <div className="text-xs text-gray-500 text-right">
          Confidence: {enricherData.confidence}%
        </div>
      )}
    </div>
  );
}

function AnalysisMetadata({ metadata }) {
  if (!metadata) return null;

  return (
    <div className="bg-white rounded-2xl p-6 shadow-xl">
      <div className="text-center text-sm text-gray-500">
        {metadata.analysis_date && `Analysis completed on ${new Date(metadata.analysis_date).toLocaleDateString()}`}
        {metadata.analysis_date && ' • '}
        Independent • No bank partnerships
        {metadata.workflow_id && (
          <span className="block mt-2 text-xs text-gray-400">
            Workflow: {metadata.workflow_id.substring(0, 12)}...
          </span>
        )}
      </div>
    </div>
  );
}

// Utility Functions - All with safe fallbacks
function formatSalary(salary) {
  if (!salary) return 'Not specified';

  const mapping = {
    'below_5000': 'Below AED 5K',
    '5000_9999': 'AED 5K-10K',
    '10000_14999': 'AED 10K-15K',
    '15000_24999': 'AED 15K-25K',
    '25000_39999': 'AED 25K-40K',
    '40000_plus': 'Above AED 40K',
  };

  return mapping[salary] || salary.toString().replace(/_/g, ' ');
}

function formatSpending(spending) {
  if (!spending) return 'Not specified';

  const mapping = {
    'below_2000': 'Below AED 2K',
    '2000_4999': 'AED 2K-5K',
    '5000_7999': 'AED 5K-8K',
    '8000_12999': 'AED 8K-13K',
    '13000_19999': 'AED 13K-20K',
    '20000_plus': 'Above AED 20K',
  };

  return mapping[spending] || spending.toString().replace(/_/g, ' ');
}

function formatEmployment(employment) {
  if (!employment) return 'Not specified';
  return employment.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
}

function formatPriority(priority) {
  if (!priority) return 'Not specified';

  const mapping = {
    'travel_benefits': 'Travel Benefits',
    'high_cashback': 'High Cashback',
    'no_annual_fees': 'No Annual Fees',
    'dining_entertainment': 'Dining & Entertainment',
    'low_foreign_fees': 'Low Foreign Transaction Fees',
    'premium_lifestyle': 'Premium Lifestyle',
  };

  return mapping[priority] || priority.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
}

function formatIncome(income) {
  if (!income) return 'Not specified';
  if (income.toString().includes('AED')) return income;
  return `AED ${income}/month`;
}

function formatFee(fee) {
  if (fee === 0) return 'Free';
  if (fee === null || fee === undefined) return 'N/A';
  if (fee.toString().includes('AED')) return fee;
  return `AED ${fee}`;
}

function formatAnnualFee(fee) {
  if (fee === 0) return 'Free';
  if (fee === null || fee === undefined) return 'N/A';
  if (typeof fee === 'string' && fee.includes('AED')) return fee;
  return `AED ${fee}`;
}

function extractMonthlyValue(monthlyValue) {
  if (!monthlyValue) return 'N/A';

  try {
    // Extract the higher value from ranges like "AED 220–500+"
    const match = monthlyValue.match(/AED\s+(\d+)(?:–(\d+))?/);
    if (match) {
      return match[2] ? `AED ${match[2]}+` : `AED ${match[1]}`;
    }

    // Try to extract just the first AED amount
    const simpleMatch = monthlyValue.match(/AED\s+(\d+)/);
    if (simpleMatch) {
      return `AED ${simpleMatch[1]}`;
    }

    return 'High Value';
  } catch (error) {
    return 'N/A';
  }
}